import BaseObjectDirective from './BaseObjectDirective';

/**
 * Controller for handling MathJax bindings.
 * This class watches for changes in the MathML expressions and updates the DOM accordingly.
 */
export class MathjaxBindController {
    /**
     * Constructor for MathjaxBindController.
     * @param {Object} $scope - Angular scope.
     * @param {Object} $element - Angular element.
     * @param {Object} $attrs - Angular attributes.
     */
    constructor($scope, $element, $attrs) {
        this.$scope = $scope;
        this.$element = $element;
        this.$attrs = $attrs;

        // Watch for changes in the MathML expression
        this.$scope.$watch(() => this.$attrs.mathjaxBind, (newVal, oldVal) => {
            // Return if new value is the same as old value and is empty
            if (newVal === oldVal && newVal.length === 0) return;

            // Modify the MathML expression to include display style
            const mathMLExpression = newVal.substr(0, 5) + ' displaystyle="true" ' + newVal.substr(5);

            // Clear the element and append the new MathML expression
            this.$element.html("");
            this.$element.append(mathMLExpression);
            this.$element.parent()[0].style.visibility = 'hidden';

            console.log("Reprocessing", mathMLExpression);

            // Callback function when typesetting is done
            const onTypesetDone = () => {
                console.log("MathML Typeset done", mathMLExpression, this.$element);
            };
            MathJax.Hub.Queue(["Typeset", MathJax.Hub, this.$element[0]], [onTypesetDone]);

            // Queue to handle element display and font size adjustment
            MathJax.Hub.Queue(() => {
                this.$element[0].style.display = "inline";
                const w = this.$element[0].offsetWidth;
                this.$element[0].style.display = "";

                console.log(this.$element[0].offsetWidth, w);
                if (this.$element[0].offsetWidth < w) {
                    this.$element[0].style.fontSize = Math.floor(this.$element[0].offsetWidth / w * 100) + "%"
                }

                // Function to show the element after rerendering
                const showElement = () => {
                    this.$element.parent()[0].style.visibility = 'visible';
                };

                MathJax.Hub.Queue(["Rerender", MathJax.Hub, this.$element[0]], [showElement]);
            });
        });
    }
}

/**
 * Controller for handling ASF Wiris objects.
 * Extends BaseObjectDirective and handles initialization and preferences.
 */
export class AsfWirisObjectCtrl extends BaseObjectDirective {
    /**
     * Constructor for AsfWirisObjectCtrl.
     * @param {Object} $injector - Angular injector.
     * @param {Object} $scope - Angular scope.
     * @param {Object} $rootScope - Angular root scope.
     * @param {Object} $signals - Angular signals.
     * @param {Object} $timeout - Angular timeout.
     * @param {Object} $bridges - Angular bridges.
     * @param {Object} ObjectInstanceRegistrationService - Service for object registration.
     * @param {Object} SheetLoadedService - Service for sheet loading.
     * @param {Object} $q - Angular promises.
     */
    constructor($injector, $scope, $rootScope, $signals, $timeout, $bridges, ObjectInstanceRegistrationService, SheetLoadedService, $q) {
        super();
        this.$injector = $injector;
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.$timeout = $timeout;
        this.$bridges = $bridges;
        this.ObjectInstanceRegistrationService = ObjectInstanceRegistrationService;
        this.SheetLoadedService = SheetLoadedService;
        this.$q = $q;

        // Check if contentObject is defined and not null
        if ((typeof this.$scope.contentObject === 'undefined') || this.$scope.contentObject === null) {
            return;
        }

        this.$scope.objectUUID = this.$scope.contentObject.getUUID();
        this.$scope.isStimulus = this.$scope.contentObject.getObjectType() === 'wirisstimulus';

        this.$injector.invoke(this.init, this, { $scope: this.$scope });
        this.$logger = this.$bridges.logger();

        this.$scope.isCMS = this.$rootScope.isCMS;
        this.$scope.editMode = false;
        this.$scope.prefsMode = false;
        this.$scope.hasEditMode = this.$scope.isStimulus;
        this.parentContainer = this.$scope.$parent.containerObj;

        this.$scope.iFrameStyle = {
            'width': '100%',
            'height': '410px',
            'border': '0'
        };

        this.initPreferences();

        // Function to check if MathML is empty
        this.isMathMLEmpty = (mathML) => {
            return typeof (mathML) === 'undefined' || mathML == null || mathML.trim() === '' || mathML == '<math xmlns="http://www.w3.org/1998/Math/MathML"/>';
        };

        this.dataToFillOnInit = null;

        // Check if contentObject is a stimulus and has content
        if (this.$scope.isStimulus && angular.isString(this.$scope.contentObject.getContent()) && this.$scope.contentObject.getContent() != '') {
            this.dataToFillOnInit = this.$scope.contentObject.getContent();
        }

        this.id = "wiris" + Math.random();

        // Normalize MathML entities if not a stimulus
        if (!this.$scope.isStimulus) {
            this.normaliseMathMLEnties = (mathML) => {
                if (typeof (mathML) === 'undefined' || mathML === null || mathML == '') {
                    return mathML;
                }

                const entity_regex = /&(?:#x[a-f0-9]+|#[0-9]+|[a-z0-9]+);?/ig;
                const convert = (entity) => {
                    return String.fromCharCode(entity);
                }
                const mathMLnormalised = mathML.replace(entity_regex, function (entity) {
                    return convert(entity) || entity;
                });
                return mathMLnormalised.replace(/(\r\n|\n|\r)/gm, "");
            };

            // Set response fill function
            this.setResponseFill((attachment) => {
                if (!attachment.mathML) return;

                if (this.wiris !== null) {
                    console.log("Setting mathML", attachment.mathML, this.id, this.$scope.contentObject.data);
                    this.wiris.setMathML(attachment.mathML);
                } else {
                    if (!this.$scope.isPDFMode) {
                        console.log("Can't fill yet, remembering", this.id, this.$scope.contentObject.data);
                        this.dataToFillOnInit = attachment.mathML;
                    }
                    this.$scope.mathML = attachment.mathML;
                }
            });

            // Set response collect function
            this.setResponseCollect((attachment, triggeredBy) => {
                if (this.$scope.isPDFMode) {
                    console.log("Skipping collection in PDF Mode");
                }
                if (this.dataToFillOnInit !== null) {
                    console.log("Skipping collect, not filled yet", this.id, this.$scope.contentObject.data);
                    return false;
                }
                if (this.wiris === null) {
                    console.log("Can't collect", this.id, this.$scope.contentObject.data)
                    return false;
                }

                console.log("Collecting mathML", this.$scope.contentObject.data, triggeredBy);
                const newMathML = this.wiris.getMathML();
                if (this.isMathMLEmpty(newMathML) && triggeredBy != "inputSave") {
                    console.log("Not saving empty mathML - change not triggered by inputSave", newMathML);
                    attachment.mathML = "";
                } else {
                    attachment.mathML = this.wiris.getMathML();
                }

                console.log("Orig mathML content", this.$scope.contentObject.getContent());
                console.log("New mathML content", newMathML);

                return !this.isMathMLEmpty(attachment.mathML)
                    && this.normaliseMathMLEnties(attachment.mathML) != this.normaliseMathMLEnties(this.$scope.contentObject.getContent());
            });
        }

        this.wiris = null;

        // Function to check if online
        this.isOnline = () => {
            if (!this.isCMS) {
                return false;
            }
            return window.location.protocol == 'https:' || window.location.protocol == 'http:';
        };

        const o = this.isOnline() ? "Online" : "Offline";
        this.$scope.wirisURL = "/iframe-tools/wiris/wiris" + o + ".html?uuid=" + this.$scope.objectUUID;

        if (this.$scope.isStimulus) {
            this.$scope.wirisURL += '&toolbar=long';
        } else {
            this.$scope.wirisURL += '&toolbar=short';
        }

        if (this.$rootScope.i18n.languageCode) {
            this.$scope.wirisURL += '&language=' + this.$rootScope.i18n.languageCode;
        }

        if (this.$rootScope.currentColorThemeObj && (!this.isCMS || this.$rootScope.isPreview)) {
            if (this.$rootScope.currentColorThemeObj.bgColor) {
                this.$scope.wirisURL += '&bg=' + this.$rootScope.currentColorThemeObj.bgColor;
            }
            if (this.$rootScope.currentColorThemeObj.color) {
                this.$scope.wirisURL += '&color=' + this.$rootScope.currentColorThemeObj.color;
            }
            this.$scope.wirisURL += '&class=' + this.$rootScope.currentColorThemeObj.class;
        }

        console.log(this.$scope.wirisURL);

        this.$scope.mathML = angular.isString(this.$scope.contentObject.getContent()) ? this.$scope.contentObject.getContent() : "";
        this.INPUT_AUTOSAVE_DELAY = 2000;
        this.lastChangeTS = new Date().getTime();
        this.listener = {
            'caretPositionChanged': (source) => {
                //console.log('caretPositionChanged', source);
            },
            'clipboardChanged': (source) => {
                //console.log('clipboardChanged', source);
            },
            'contentChanged': (source) => {
                console.log('mathML contentChanged', source);

                this.$scope.mathML = this.wiris.getMathML();

                if (this.isCMS && !this.$rootScope.isPreview) {
                    console.log("Saving mathml CMS content change", this.$scope.mathML)
                    this.$scope.$apply(() => {
                        this.$scope.contentObject.setContent(this.$scope.mathML);
                    });
                } else {
                    const curTS = new Date().getTime(),
                        deltaMS = curTS - this.lastChangeTS;
                    if (deltaMS < this.INPUT_AUTOSAVE_DELAY) {
                        return;
                    }
                    this.lastChangeTS = curTS;

                    this.$timeout(() => {
                        this.$scope.$emit(SIGNAL_INPUT_SAVE);
                    }, this.INPUT_AUTOSAVE_DELAY);
                }
            },
            'styleChanged': (source) => {
                //console.log('styleChanged', source);
            },
            'transformationReceived': (source, transformation) => {
                //console.log('transformationReceived', source, transformation);
            }
        };

        this._d = this.$q.defer();
        this.SheetLoadedService.wait(this._d.promise, 'wiris' + (this.$scope.isStimulus ? '-stimulus' : ''));

        if (this.$scope.isStimulus) {
            this._d.resolve('wiris-stimulus');
        }

        // Function to handle iframe ready event
        this.onIframeReady = (objectUUID) => {
            if (typeof objectUUID != 'undefined' && objectUUID == this.$scope.objectUUID) {
                this.wiris = this.ObjectInstanceRegistrationService.getObjectByUUID(this.$scope.objectUUID);

                this.wiris.getEditorModel().addEditorListener(this.listener);
                this.$timeout(() => {
                    this._d.resolve('wiris');
                });

                if (this.dataToFillOnInit !== null) {
                    console.log("Filling on init", this.dataToFillOnInit);
                    this.wiris.setMathML(this.dataToFillOnInit);
                    this.dataToFillOnInit = null;
                } else if (angular.isString(this.$scope.contentObject.getContent()) && this.$scope.contentObject.getContent() != '') {
                    if (this.wiris.getMathML() == '<math xmlns="http://www.w3.org/1998/Math/MathML"/>') {
                        this.wiris.setMathML(this.$scope.contentObject.getContent());
                    }
                }
            }
        };
        this.$signals.on('iFrameReady', this.onIframeReady);

        this.$scope.$on('$destroy', () => {
            this.$signals.remove('iFrameReady', this.onIframeReady);
        });

        this.$scope.resetChanges = () => {
            console.log("override # <" + this.$scope.contentObject.getUUID() + "> resetChanges()");
        };
    }

    /**
     * Initializes preferences for the ASF Wiris object.
     */
    initPreferences() {
        const includedInPDF = (typeof this.$scope.contentObject.getPreferences() !== "undefined" ? this.$scope.contentObject.getPreferences().includedInPDF : true);
        const targetHintObject = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().targetHintObject : undefined);

        this.$scope.$watch('preferencesValues', (val1, val2) => {
            if (val1 == val2) return;
            angular.forEach(this.$scope.preferencesValues, (value, key) => {
                this.$scope.contentObject.setPreference(key, value);
            });
        }, true);

        this.$scope.preferencesValues = {};
        this.$scope.preferencesValues['includedInPDF'] = typeof includedInPDF !== 'undefined' ? includedInPDF : true;
        this.$scope.preferencesValues['targetHintObject'] = targetHintObject;

        this.$scope.preferences = [
            {
                'includedInPDF': [
                    { 'type': 'checkbox', 'name': 'Included in Response PDF', 'valueKey': 'includedInPDF' }
                ],
                'targetHintObject': [{
                    'type': 'select-other-content-object',
                    'name': ' link this Object as Hint',
                    'valueKey': 'targetHintObject',
                    'objectType': 'hint',
                    'parentContainer': this.parentContainer
                }]
            },
        ];
    }
}

/**
 * Registers the MathJax bind directive.
 * @param {Object} module - Angular module.
 */
export function registerMathjaxBindDirective(module) {
    module.directive('mathjaxBind', function () {
        return {
            restrict: 'A',
            controller: MathjaxBindController
        };
    });
}

/**
 * Registers the ASF Wiris object directive.
 * @param {Object} module - Angular module.
 */
export function registerAsfWirisObjectDirective(module) {
    module.directive('asfWirisObject', function ($bridges, $config, TaskDataService, $rootScope, $signals) {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                "contentObject": "="
            },
            templateUrl: "/views/ib/contentobjects/wiris.html",
            controller: AsfWirisObjectCtrl
        };
    });
    registerMathjaxBindDirective(module);
}

/**
 *
* Registers the ASF Wiris stimulus object directive.
 * @param {Object} module - Angular module.
 */
export default function registerAsfWirisstimulusObjectDirective(module) {
    module.directive('asfWirisstimulusObject', function ($bridges, $config, TaskDataService, $rootScope, $signals) {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                "contentObject": "="
            },
            templateUrl: "/views/ib/contentobjects/wiris.html",
            controller: AsfWirisObjectCtrl
        };
    });

    registerContentObject(module);
}

/**
 * Registers the content object.
 * @param {Object} module - Angular module.
 */
export function registerContentObject(module) {
    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        const obj = [{
            "uuid": "",
            "content": "",
            "objectType": "wiris",
            "type": "contentObject",
            "entity_content": {
                "content": ""
            },
            "preferences": {
                "includedInPDF": true
            }
        }];
        const objProperties = {
            type: "wiris",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.WIRIS.LABEL',
            cssClass: "new-wiris-object",
            icon: "cms/icon-wiris-equation.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.WIRIS.DESCRIPTION',
            group: {
                id: 1
            }
        };
        ContentObjectRegistryService.register(obj, objProperties);
    }]);

    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        const obj = [{
            "uuid": "",
            "content": "",
            "objectType": "wirisstimulus",
            "type": "contentObject",
            "entity_content": {
                "content": ""
            },
            "preferences": {
                "includedInPDF": false
            }
        }];
        const objProperties = {
            type: "wirisstimulus",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.WIRIS-STIMULUS.LABEL',
            cssClass: "new-wiris-object",
            icon: "cms/icon-wiris-stimulus.png", /*CONTINUED*/
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.WIRIS-STIMULUS.DESCRIPTION',
            group: {
                id: 1
            }
        };
        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}